import { isSpectator, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { MeetingSessionHighlightingOnResponseActionHandler, MeetingSessionHighlightingState, SocketActionHandler } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMeetingUtilsContext } from "../contexts/meetingUtils/context";
import { isExpert } from "../helpers/roles";
import { HighlightedSessions } from "../interfaces/highlight";
import { toastError, toastInfo, toastSuccess } from "../helpers/toast";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { useSdkContext } from "../contexts/sdk/context";
import { useAppStoreSelector } from "../contexts/appStore/selectors";
import { useAppStoreDispatch } from "../contexts/appStore/context";
import { useFeatures } from "./features";
import { buildMediasFromSources, MeetingSourceExtended, useMeetingSources } from "./meeting";

export const useHighlightSessions = (params?: { onNewRequests?: () => void }): HighlightedSessions => {
  const { populated, containerAPI } = useMeetingUtilsContext();
  const [sessionId] = useMeetingSelector(containerAPI, "session", s => s?.id);

  const seenRequests = useRef(new Set<string>());

  const confirmedSessionsSet = useMemo(() => {
    const set = new Set<string>();
    populated.activity?.confirmedSessions?.forEach(s => set.add(s.id));
    return set;
  }, [populated.activity?.confirmedSessions]);

  const sessions = useMemo(() => {
    const output: HighlightedSessions = {
      ongoing: [],
      requesting: [],
      inactive: [],
    };

    populated.users?.forEach((pUser) => {
      pUser.sessions?.forEach((incSession) => {
        if (isExpert(incSession.session)) return;
        if (incSession.id === sessionId) return;
        switch (incSession.session.highlighting) {
          case MeetingSessionHighlightingState.PENDING_REQUEST:
            output.requesting.push(incSession);
            break;
          case MeetingSessionHighlightingState.ONGOING:
            const pending = !confirmedSessionsSet.has(incSession.id) || !incSession.session.medias.length;
            output.ongoing.push({ session: incSession, pending });
            break;
          default:
            output.inactive.push(incSession);
        }
      });
    });

    return output;
  }, [populated.users, confirmedSessionsSet, sessionId]);

  useEffect(() => {
    if (!params?.onNewRequests) return;

    const newRequests = new Set<string>();
    sessions.requesting.forEach(s => {
      newRequests.add(s.id);
    });

    // check if there is some requests that were not in the previous seen
    
    let hasNewRequests = false;
    newRequests.forEach(id => {
      if (!seenRequests.current.has(id)) hasNewRequests = true;
    });
    if (hasNewRequests) params.onNewRequests();

    seenRequests.current = newRequests;
    
  }, [sessions.requesting]);

  return sessions;
};

export type HighlightMediaSelectionMode = "none" | "requesting" | "confirming";

export interface HighlightMediaSelectionState {
  mode: HighlightMediaSelectionMode;
  requestingHighlight: boolean;
  remainingSeconds?: number;
  onConfirm: () => Promise<void>;
  onCancel: () => Promise<void>;
  startRequest: () => void;
}

export const useHighlightNotifications = (): HighlightMediaSelectionState => {
  const { populated, containerAPI } = useMeetingUtilsContext();
  const [session] = useMeetingSelector(containerAPI, "session");
  const [meetingId] = useMeetingSelector(containerAPI, "meeting", meeting => meeting?.id);

  const sdkCtx = useSdkContext();
  const sdkCtxRef = useRefAutoUpdate(sdkCtx);
  const features = useFeatures();
  const sources = useMeetingSources();
  const dispatch = useAppStoreDispatch();
  const dispatchRef = useRefAutoUpdate(dispatch);
  const sourcePreferences = useAppStoreSelector(state => state.sourcePreferences);
  const globalAudio = useAppStoreSelector(state => state.audioStreamEnabled);
  const globalVideo = useAppStoreSelector(state => state.videoStreamEnabled);
  const containerAPIRef = useRefAutoUpdate(containerAPI);

  /**
   * If i a spectator :
   * - i need the popup to set medias AND confirm participation
   * If i am not spectator :
   * - i need popup to confirm participation
   */
  const [forcePopupForConfirmation, setForcePopupForConfirmation] = useState(false);

  const [requestingHighlight, setRequestingHighlight] = useState(false);
  const requestedHighlightAt = useRef<Date>();

  const selfActivity = useMemo(() => {
    if (!session) return undefined;

    // No ongoing activity
    if (!populated.activity) return undefined;

    // I am not concerned by the activity
    if (!populated.activity.sessions.some(s => s.id === session.id)) return undefined;

    return {
      // I confirmed my participation
      activity: populated.activity,
      confirmed: populated.activity.confirmedSessions.some(s => s.id === session.id),
    };
  }, [populated.activity, session]);

  const meetingIdRef = useRefAutoUpdate(meetingId);
  const sessionRef = useRefAutoUpdate(session);
  const selfActivityRef = useRefAutoUpdate(selfActivity);
  const sourcesRef = useRefAutoUpdate(sources);
  const sourcePreferencesRef = useRefAutoUpdate(sourcePreferences);
  const globalAudioRef = useRefAutoUpdate(globalAudio);
  const globalVideoRef = useRefAutoUpdate(globalVideo);

  const iAmRequestingRef = useRef(session?.highlighting === MeetingSessionHighlightingState.PENDING_REQUEST);
  const onBoardRef = useRef(selfActivity?.confirmed ?? false);

  ////////////////// UTILS //////////////////

  const onJoinActivity = useCallback(async () => {
    const currentMeetingId = meetingIdRef.current;
    const currentSession = sessionRef.current;
    if (!currentMeetingId || !currentSession) return;
    const api = containerAPIRef.current;

    // If spectator with no media, create media on the session before enlisting
    if (api && isSpectator(currentSession) && (!currentSession.medias || currentSession.medias.length === 0)) {
      const medias = buildMediasFromSources((sourcesRef.current ?? []) as MeetingSourceExtended[], {
        getSourceEnabled: (sourceName) => {
          const pref = sourcePreferencesRef.current[sourceName];
          return {
            audio: pref?.audioEnabled ?? globalAudioRef.current,
            video: pref?.videoEnabled ?? globalVideoRef.current,
          };
        },
      });
      for (const media of medias) {
        await api.updater.medias.create(currentSession.id, media);
      }
    }

    const client = sdkCtxRef.current.client.getWebsocketClient();

    if (!selfActivityRef.current?.confirmed) {
      const response = await client.emitter.meeting.enlistInActivityRoom({
        id: currentMeetingId,
      });
      if (response.error || !response.response) {
        // little race-condition can happen here
        //
        // 1. we receive the session status update and we enter here
        // 2. the backend auto-enlists (within 30s)
        // 3. we fail here because already enlisted
        // 
        // we can double check again here again so we don't show the error
        //
        // This is a temp fix, backend auto-enlist will be removed, only kept for now
        // for escaet compat
        if (selfActivityRef.current?.confirmed) return;
        toastError("Échec de l'intervention");
        return;
      }
    }

    setForcePopupForConfirmation(false);
  }, []);

  const onRemoveHighlight = useCallback(async () => {
    if (!sessionRef.current) return;
    const client = sdkCtxRef.current.client.getWebsocketClient();
    const response = await client.emitter.meeting.stopHighlighting({
      sessionId: sessionRef.current.id,
    });
    if (response.error || !response.response) {
      toastError("Une erreur est survenue");
      return;
    }
    setForcePopupForConfirmation(false);
  }, []);

  const startRequest = useCallback(() => {
    setRequestingHighlight(true);
  }, []);

  ////////////////// HOOKS //////////////////

  // Clear requestingHighlight when highlighting changes from INACTIVE
  useEffect(() => {
    if (session?.highlighting !== MeetingSessionHighlightingState.INACTIVE) {
      setRequestingHighlight(false);
    }
  }, [session?.highlighting]);

  useEffect(() => {
    if (session?.highlighting === MeetingSessionHighlightingState.PENDING_REQUEST) {
      iAmRequestingRef.current = true;
    }
  }, [session?.highlighting]);

  useEffect(() => {
    if (selfActivity?.confirmed) {
      if (iAmRequestingRef.current) {
        toastSuccess("Votre demande d'intervention a été acceptée");
      } else {
        toastSuccess("Confirmation de votre intervention");
      }
      onBoardRef.current = true;
    } else {
      if (onBoardRef.current) {
        toastInfo("Fin de votre intervention");
        onBoardRef.current = false;
      }
    }
    iAmRequestingRef.current = false;
  }, [selfActivity?.confirmed]);

  useEffect(() => {
    const client = sdkCtxRef.current.client.getWebsocketClient();

    const handlers: SocketActionHandler[] = [];

    const responseHandler = new MeetingSessionHighlightingOnResponseActionHandler();
    responseHandler.onReceive = (event) => {
      if (!event.content.accepted) {
        toastInfo("Votre demande d'intervention a été refusée");
      }
    }
    handlers.push(responseHandler);

    client.registerActionHandlers(handlers);
    return () => client.unregisterActionHandlers(handlers);
  }, []);

  const [remainingSeconds, setRemainingSeconds] = useState<number | undefined>(undefined);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const onJoinActivityRef = useRefAutoUpdate(onJoinActivity);

  // Force show media selection when:
  // I am highlighted but either :
  // - I did not confirm
  // - I don't have medias
  const shouldForcePopupForConfirmation = session?.highlighting === MeetingSessionHighlightingState.ONGOING && (
    !selfActivity?.confirmed
    || !session.medias?.length
  );

  useEffect(() => {
    if (session?.highlighting === MeetingSessionHighlightingState.INACTIVE) {
      requestedHighlightAt.current = undefined;
    }
  }, [session?.highlighting]); 

  useEffect(() => {
    if (shouldForcePopupForConfirmation) {
      if (requestedHighlightAt.current && Date.now() - requestedHighlightAt.current.getTime() < 30000) {
        // Student requested himself so he already configured media during pre-request
        // if it's more recent than 30s, we can auto-join
        //
        // The selfActivity?.confirmed is important because even tho i requested, maybe it was older
        // than 30s and the backend higlighted only, without enlisting, so i have the popup again
        onJoinActivityRef.current();
        requestedHighlightAt.current = undefined;
      } else {
        setForcePopupForConfirmation(true);
      }
    } else {
      setForcePopupForConfirmation(false);
      setRemainingSeconds(undefined);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [shouldForcePopupForConfirmation]);

  // Auto-confirm timer (optional via feature flag, only in confirming mode)
  useEffect(() => {
    if (!forcePopupForConfirmation) return;
    const duration = features.highlightAutoAcceptTimer;
    if (duration === undefined) return;

    setRemainingSeconds(duration);
    timerRef.current = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev === undefined) return undefined;
        const next = prev - 1;
        if (next <= 0) {
          if (timerRef.current) clearInterval(timerRef.current);
          onJoinActivityRef.current();
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [forcePopupForConfirmation, features.highlightAutoAcceptTimer]);

  const mode = useMemo<HighlightMediaSelectionMode>(() => {
    if (requestingHighlight) return "requesting";
    if (forcePopupForConfirmation) return "confirming";
    return "none";
  }, [requestingHighlight, forcePopupForConfirmation]);

  // Entering the board flow: turn the mic and camera on. Besides being the
  // expected state when going on board, this starts the captures, which
  // prompts for the missing permissions — a denied permission surfaces as the
  // crossed-out toggle + error toast, and the user can still switch them back
  // off before confirming. Only on `mode` transitions (through refs): reacting
  // to anything else would override the user's toggles right after each click.
  useEffect(() => {
    if (mode === "none") return;
    (sourcesRef.current ?? []).forEach((source) => {
      if (!source.name) return;
      dispatchRef.current("updateSourcePreference", { sourceName: source.name, audioEnabled: true, videoEnabled: true });
    });
    dispatchRef.current("setAudioStreamEnabled", true);
    dispatchRef.current("setVideoStreamEnabled", true);
  }, [mode]);

  const onConfirm = useCallback(async () => {
    if (requestingHighlight) {
      // Pre-request: send the highlight request
      if (!session) return;
      const client = sdkCtxRef.current.client.getWebsocketClient();
      const response = await client.emitter.meeting.requestForHighlighting({
        sessionId: session.id,
      });
      if (response.error || !response.response) {
        toastError("Une erreur est survenue...");
        setRequestingHighlight(false);
        return;
      }
      requestedHighlightAt.current = new Date();
      setRequestingHighlight(false);
    } else {
      // Post-accept: enlist in activity
      await onJoinActivity();
    }
  }, [requestingHighlight, session, sdkCtxRef, onJoinActivity]);

  const onCancel = useCallback(async () => {
    if (requestingHighlight) {
      // Pre-request: just close the popup
      setRequestingHighlight(false);
    } else {
      // Post-accept: refuse/stop highlighting
      await onRemoveHighlight();
    }
  }, [requestingHighlight, onRemoveHighlight]);

  return {
    mode,
    requestingHighlight,
    remainingSeconds,
    onConfirm,
    onCancel,
    startRequest,
  };
};
